// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { ActionIcon, Flex, Pill, Stack, Text, TextInput } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNotNil } from 'helpers/isNotNil';

type Props = {
  value?: string[];
  onChange: (value: string[]) => void;
  maxValueLength?: number;
  validateValue?: (value: string) => undefined | string;
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
  required?: boolean;
  disabled?: boolean;
  description?: string;
};

export const MultiOptionCreator = ({
  maxValueLength = 64,
  label,
  placeholder,
  validateValue,
  value: parentValue,
  onChange: parentOnChange,
  required,
  autoFocus = required,
  description,
  disabled,
}: Props) => {
  const [typingValue, setTypingValue] = useState('');
  const [error, setError] = useState('');

  const [value, onChange] = useUncontrolled({
    value: parentValue,
    onChange: parentOnChange,
  });

  const onValueAdd = () => {
    const textValue = typingValue.trim();
    if (!textValue) return;
    const customError = validateValue?.(textValue);
    if (customError) {
      setError(customError);
      return;
    }
    const existing = value.find((v) => v.toLowerCase() === textValue.toLowerCase());
    if (isNotNil(existing)) {
      setError(`${existing} already exists.`);
    } else {
      onChange([...value, textValue]);
      setTypingValue('');
    }
  };

  return (
    <Stack gap="sm">
      <Flex gap="sm" align="flex-end">
        <TextInput
          label={label}
          placeholder={placeholder}
          autoFocus={autoFocus}
          // Only the visual asterisk — the HTML `required` attribute would make native form
          // validation block a surrounding <form> (e.g. FormPopover/FormModal) from submitting,
          // and this typing input is intentionally empty once its text is committed as a pill.
          withAsterisk={required}
          description={description}
          data-autofocus={autoFocus}
          value={typingValue}
          onChange={(e) => {
            setError('');
            setTypingValue(e.target.value);
          }}
          maxLength={maxValueLength}
          onBlur={onValueAdd}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onValueAdd();
              e.stopPropagation();
              e.preventDefault();
            }
          }}
          style={{ width: '100%' }}
          disabled={disabled}
          error={error}
        />
        <AgGridStyleTooltip label="You can also add options by hitting enter." openDelay={0} withArrow>
          <ActionIcon size="input-sm" variant="light" disabled={!typingValue || disabled} onClick={onValueAdd}>
            <EvolveIcon icon="Add" size="sm" />
          </ActionIcon>
        </AgGridStyleTooltip>
      </Flex>
      <Flex gap="xs" wrap="wrap">
        {required && value.length === 0 && (
          <Text c="dimmed" fz="xs">
            Add at least one option above.
          </Text>
        )}
        {value.map((v, i) => (
          <Pill
            key={v}
            withRemoveButton
            disabled={disabled}
            onRemove={() => {
              onChange([...value.slice(0, i), ...value.slice(i + 1)]);
            }}
          >
            {v}
          </Pill>
        ))}
      </Flex>
    </Stack>
  );
};
